import { Box, Typography, List, Modal, ListItem, Tooltip } from '@mui/material';
import { Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { MAX_FILE_SIZE_MB } from '@/utils/constants';

interface AssetModalProps {
  open?: boolean;
  onClose?: () => void;
  orientation?: '16:9' | '9:16';
  durationList?: (number | undefined)[];
  labelText?: string;
  isTooltip?: boolean;
}

export default function AssetGuidelinesModal({
  open = false,
  onClose,
  orientation,
  durationList,
  isTooltip = false,
  labelText = 'Asset Guidelines',
}: AssetModalProps) {
  const content = (
    <>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: 'secondary.main',
        }}
      >
        {orientation === '16:9'
          ? 'Landscape Asset Guidelines'
          : 'Portrait Asset Guidelines'}
      </Typography>
      <List
        component="div"
        aria-labelledby="landscape-img-requirement"
        sx={{
          listStyleType: 'disc',
          listStylePosition: 'inside',

          '& .MuiListItem-root': {
            fontSize: '14px',
            color: 'secondary.main',
            paddingLeft: '8px',
            paddingRight: '0px',
            paddingBottom: 0,
          },
        }}
      >
        <ListItem
          sx={{
            display: 'list-item',
            color: 'fileInput.light',

            paddingBottom: 0,
          }}
        >
          Supported file types: png, jpg, mp4
        </ListItem>
        <ListItem
          sx={{
            display: 'list-item',
          }}
        >
          Video duration: {durationList?.[0] ?? 6}s to{' '}
          {durationList?.[durationList.length - 1] ?? 30}s
        </ListItem>
        <ListItem
          sx={{
            display: 'list-item',
          }}
        >
          Minimum resolution:{' '}
          {orientation === '16:9' ? '1920x1080 px' : '1080x1920 px'}
        </ListItem>

        <ListItem
          sx={{
            display: 'list-item',
          }}
        >
          Max file size: {MAX_FILE_SIZE_MB}MB
        </ListItem>
      </List>
    </>
  );

  if (isTooltip) {
    return (
      <Tooltip
        title={content}
        data-testid="asset-guidelines-tooltip"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'asset.light',
              position: 'absolute',
              borderRadius: '8px',
              padding: '24px',
              width: '320px',
              overflowY: 'auto',
            },
          },
        }}
        placement="bottom-start"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [40, -5],
                },
              },
            ],
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'secondary.dark',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Info className="mr-1 size-4" />
          {labelText}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        justifyContent: 'space-around',
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          bgcolor: 'asset.light',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '8px',
          padding: '24px',
          width: '320px',
          overflowY: 'auto',
        }}
      >
        {content}
        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{
            marginTop: '30px',
          }}
        >
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
